import type { Dash, Primitive, Stroke, TextStyle } from '@vp/shared-types';
import type { GeoEdge, GeoFrame, GeoGroup, GeoNode, Geometry } from './geometry';
import { formOutline, formTextInset, type RectForm } from './shapes';
import { THEME } from './theme';

/**
 * Geometry → platform primitives (diagram coordinates). Module node forms are drawn as a dashed
 * placeholder here; the platform swaps in the module's own drawing when it is available.
 */
export function toPrimitives(g: Geometry): Primitive[] {
  return padText(build(g));
}

/**
 * The platform insets text by 0.45 × font size on each side (see render textPadding); layout boxes are
 * measured tight, so text boxes are widened by that inset to keep words on one line.
 */
function padText(prims: Primitive[]): Primitive[] {
  return prims.map((p) => {
    if (p.k !== 'text') return p;
    const pad = p.style.fontSize * 0.45 + 1;
    return { ...p, x: p.x - pad, w: p.w + pad * 2 };
  });
}

function build(g: Geometry): Primitive[] {
  const out: Primitive[] = [];
  for (const it of g.items) {
    if (it.k === 'group') group(out, it);
    else if (it.k === 'frame') frame(out, it);
    else if (it.k === 'edge') edge(out, it);
    else if (it.k === 'node') node(out, it);
    else out.push({ k: 'text', x: it.x, y: it.y, w: it.w, h: it.h, text: it.text, style: { fontSize: it.fontSize, color: it.color, align: it.align, valign: 'middle', bold: it.bold } });
  }
  return out;
}

const solid = (color: string) => ({ kind: 'solid' as const, color });
const strokeOf = (color: string, width: number, dash: Dash = 'solid'): Stroke => ({ color, width, dash });

function textStyle(n: { fontSize: number; text: string; bold?: boolean }, align: TextStyle['align'] = 'center'): TextStyle {
  return { fontSize: n.fontSize, color: n.text, align, valign: 'middle', bold: n.bold, lineHeight: 1.3 };
}

function formNode(out: Primitive[], n: GeoNode, form: RectForm): void {
  const { x, y, w, h } = n;
  const fill = solid(n.fill);
  const st = strokeOf(n.stroke, n.width, n.strokeDash);
  const flip = !!n.flip;
  const { points, closed } = formOutline(form, w, h, Math.min(6, h / 8), flip);
  const pts = points.map((v, i) => (i % 2 === 0 ? v + x : v + y));
  out.push({ k: 'path', points: pts, closed, fill: closed ? fill : { kind: 'none' }, stroke: st });
  const ins = formTextInset(form, w, h, flip);
  const align = form === 'bracket' || form === 'brace' || form === 'display' ? 'left' : 'center';
  label(out, n, x + ins.left, y + ins.top, w - ins.left - ins.right, h - ins.top - ins.bottom, align);
}

function label(out: Primitive[], n: GeoNode, x = n.x, y = n.y, w = n.w, h = n.h, align: TextStyle['align'] = 'center'): void {
  if (n.label) out.push({ k: 'text', x, y, w, h, text: n.label, style: textStyle(n, align) });
}

export function node(out: Primitive[], n: GeoNode): void {
  const { x, y, w, h } = n;
  const fill = solid(n.fill);
  const st = strokeOf(n.stroke, n.width, n.strokeDash);
  const poly = (pts: number[]) => out.push({ k: 'path', points: pts, closed: true, fill, stroke: st });
  switch (n.shape) {
    case 'rect':
    case 'participant':
      out.push({ k: 'shape', shape: 'rect', x, y, w, h, radius: n.shape === 'participant' ? 6 : 3, fill, stroke: st });
      return label(out, n);
    case 'rounded':
      out.push({ k: 'shape', shape: 'rect', x, y, w, h, radius: Math.min(14, h / 3), fill, stroke: st });
      return label(out, n);
    case 'pill':
      out.push({ k: 'shape', shape: 'rect', x, y, w, h, radius: h / 2, fill, stroke: st });
      return label(out, n);
    case 'circle':
    case 'ellipse':
      out.push({ k: 'shape', shape: 'ellipse', x, y, w, h, fill, stroke: st });
      return label(out, n, x + w * 0.12, y, w * 0.76, h);
    case 'diamond':
      out.push({ k: 'shape', shape: 'diamond', x, y, w, h, fill, stroke: st });
      return label(out, n, x + w * 0.2, y, w * 0.6, h);
    case 'hexagon': {
      const k = Math.min(w / 4, h * 0.45);
      poly([x + k, y, x + w - k, y, x + w, y + h / 2, x + w - k, y + h, x + k, y + h, x, y + h / 2]);
      return label(out, n, x + k, y, w - 2 * k, h);
    }
    case 'parallelogram': {
      const k = Math.min(w / 4, h * 0.5);
      poly([x + k, y, x + w, y, x + w - k, y + h, x, y + h]);
      return label(out, n, x + k / 2, y, w - k, h);
    }
    case 'cylinder': {
      const ry = Math.min(10, h / 5);
      const body = [x, y + ry, ...arc(x + w / 2, y + h - ry, w / 2, ry, Math.PI, 0), x + w, y + ry, ...arc(x + w / 2, y + ry, w / 2, ry, 0, Math.PI)];
      out.push({ k: 'path', points: body, closed: true, fill, stroke: st });
      out.push({ k: 'shape', shape: 'ellipse', x, y, w, h: ry * 2, fill, stroke: st });
      return label(out, n, x, y + ry * 2, w, h - ry * 2);
    }
    case 'document': {
      const wave = Math.min(10, h / 5);
      const pts = [x, y, x + w, y, x + w, y + h - wave];
      for (let i = 0; i <= 20; i++) {
        const t = i / 20;
        pts.push(x + w - t * w, y + h - wave + Math.sin(t * Math.PI * 2) * wave * 0.7);
      }
      poly(pts);
      return label(out, n, x, y, w, h - wave);
    }
    case 'triangle':
      out.push({ k: 'shape', shape: 'triangle', x, y, w, h, fill, stroke: st });
      return label(out, n);
    case 'manual':
    case 'data':
    case 'bracket':
    case 'brace':
    case 'display':
      return formNode(out, n, n.shape);
    case 'actor': {
      const cx = x + w / 2;
      const head = 7;
      const top = y + 2;
      out.push({ k: 'shape', shape: 'ellipse', x: cx - head, y: top, w: head * 2, h: head * 2, fill: solid('#ffffff'), stroke: strokeOf(n.stroke, 1.5) });
      const body = (pts: number[]) => out.push({ k: 'path', points: pts, closed: false, fill: { kind: 'none' }, stroke: strokeOf(n.stroke, 1.5), round: true });
      body([cx, top + head * 2, cx, top + 30]);
      body([cx - 11, top + 20, cx + 11, top + 20]);
      body([cx - 9, top + 42, cx, top + 30, cx + 9, top + 42]);
      return label(out, { ...n, bold: true }, x, y + 44, w, h - 44);
    }
    case 'activation':
      out.push({ k: 'shape', shape: 'rect', x, y, w, h, radius: 0, fill, stroke: st });
      return;
    case 'note': {
      const f = Math.min(10, h / 3);
      poly([x, y, x + w - f, y, x + w, y + f, x + w, y + h, x, y + h]);
      out.push({ k: 'path', points: [x + w - f, y, x + w - f, y + f, x + w, y + f], closed: false, fill: { kind: 'none' }, stroke: strokeOf(n.stroke, 1) });
      out.push({ k: 'text', x: x + 8, y, w: w - 16, h, text: n.label, style: { ...textStyle(n, 'left') } });
      return;
    }
    default:
      // module form: placeholder until the module draws it
      out.push({ k: 'shape', shape: 'rect', x, y, w, h, radius: 6, fill: solid('#f8fafc'), stroke: strokeOf('#94a3b8', 1.2, 'dashed') });
      return label(out, n);
  }
}

function arc(cx: number, cy: number, rx: number, ry: number, a0: number, a1: number, steps = 20): number[] {
  const out: number[] = [];
  for (let i = 0; i <= steps; i++) {
    const a = a0 + ((a1 - a0) * i) / steps;
    out.push(cx + Math.cos(a) * rx, cy + Math.sin(a) * ry);
  }
  return out;
}

function edge(out: Primitive[], e: GeoEdge): void {
  if (e.points.length < 4) return;
  const st = strokeOf(e.color, e.width, e.dash);
  out.push({ k: 'path', points: e.points, closed: false, fill: { kind: 'none' }, stroke: st, arrowStart: e.arrowStart, arrowEnd: e.arrowEnd && !e.openArrow });
  if (e.arrowEnd && e.openArrow) {
    // open chevron for asynchronous messages
    const n = e.points.length;
    const [x1, y1, x0, y0] = [e.points[n - 2], e.points[n - 1], e.points[n - 4], e.points[n - 3]];
    const a = Math.atan2(y1 - y0, x1 - x0);
    const s = 9;
    out.push({ k: 'path', points: [x1 - Math.cos(a - 0.45) * s, y1 - Math.sin(a - 0.45) * s, x1, y1, x1 - Math.cos(a + 0.45) * s, y1 - Math.sin(a + 0.45) * s], closed: false, fill: { kind: 'none' }, stroke: strokeOf(e.color, e.width), round: true });
  }
  if (e.label) {
    const l = e.label;
    if (!l.plain) out.push({ k: 'shape', shape: 'rect', x: l.x, y: l.y, w: l.w, h: l.h, radius: 4, fill: solid('#ffffff'), stroke: null, opacity: 0.92 });
    out.push({ k: 'text', x: l.x, y: l.y, w: l.w, h: l.h, text: l.text, style: { fontSize: l.fontSize, color: l.color, align: 'center', valign: 'middle', lineHeight: 1.3 } });
  }
}

function group(out: Primitive[], g: GeoGroup): void {
  out.push({ k: 'shape', shape: 'rect', x: g.x, y: g.y, w: g.w, h: g.h, radius: 10, fill: solid(g.fill), stroke: strokeOf(g.stroke, 1.2, 'dashed') });
  out.push({ k: 'text', x: g.x + 12, y: g.y + 2, w: g.w - 24, h: g.titleH, text: g.title, style: { fontSize: g.fontSize, color: THEME.group.title, align: 'left', valign: 'middle', bold: true } });
}

function frame(out: Primitive[], f: GeoFrame): void {
  out.push({ k: 'shape', shape: 'rect', x: f.x, y: f.y, w: f.w, h: f.h, radius: 4, fill: { kind: 'none' }, stroke: strokeOf(f.stroke, 1.2) });
  const tagW = f.tag.length * f.fontSize * 0.62 + 16;
  out.push({ k: 'path', points: [f.x, f.y, f.x + tagW, f.y, f.x + tagW, f.y + 14, f.x + tagW - 8, f.y + 22, f.x, f.y + 22], closed: true, fill: solid(THEME.frame.tag), stroke: strokeOf(f.stroke, 1.2) });
  out.push({ k: 'text', x: f.x + 4, y: f.y, w: tagW - 8, h: 22, text: f.tag, style: { fontSize: f.fontSize, color: THEME.frame.text, align: 'left', valign: 'middle', bold: true } });
  if (f.label) out.push({ k: 'text', x: f.x + tagW + 8, y: f.y, w: Math.max(10, f.w - tagW - 16), h: 22, text: f.label, style: { fontSize: f.fontSize, color: THEME.frame.text, align: 'left', valign: 'middle' } });
  for (const s of f.sections) {
    out.push({ k: 'path', points: [f.x, s.y, f.x + f.w, s.y], closed: false, fill: { kind: 'none' }, stroke: strokeOf(f.stroke, 1, 'dashed') });
    if (s.label) out.push({ k: 'text', x: f.x + 10, y: s.y, w: f.w - 20, h: 22, text: s.label, style: { fontSize: f.fontSize, color: THEME.frame.text, align: 'left', valign: 'middle' } });
  }
}

/** Scale and move primitives (diagram coords → object box): sizes, fonts and strokes scale too. */
export function transformPrimitives(prims: Primitive[], s: number, dx: number, dy: number): Primitive[] {
  const st = (x: Stroke | null) => (x ? { ...x, width: x.width * s } : null);
  return prims.map((p): Primitive => {
    switch (p.k) {
      case 'shape':
        return { ...p, x: p.x * s + dx, y: p.y * s + dy, w: p.w * s, h: p.h * s, radius: p.radius !== undefined ? p.radius * s : undefined, stroke: st(p.stroke) };
      case 'path':
        return { ...p, points: p.points.map((v, i) => (i % 2 === 0 ? v * s + dx : v * s + dy)), stroke: st(p.stroke) };
      case 'text':
        return { ...p, x: p.x * s + dx, y: p.y * s + dy, w: p.w * s, h: p.h * s, style: { ...p.style, fontSize: p.style.fontSize * s } };
      case 'image':
        return { ...p, x: p.x * s + dx, y: p.y * s + dy, w: p.w * s, h: p.h * s };
      default:
        return p;
    }
  });
}
